"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Chip = exports.CHIP_SEMANTIC = void 0;
var React = _interopRequireWildcard(require("react"));
var _classify = require("../../utils/classify");
var _Icon = require("../Icon");
var _StatusIndicator = require("../StatusIndicator");
var _Truncate = require("../Truncate");
var _ChipModule = _interopRequireDefault(require("./Chip.module.css"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const CHIP_SEMANTIC = Object.freeze({
  primary: 'primary',
  information: 'information',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  secondary: 'secondary'
});
exports.CHIP_SEMANTIC = CHIP_SEMANTIC;
const Chip = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    classNames,
    semantic = 'primary',
    size = 'medium',
    children,
    iconName = '',
    iconType = 'regular',
    showStatusIndicator,
    dismissable = false,
    onDismiss = () => null,
    onClick,
    disabled,
    disableHoverState = !onClick,
    // There is no reason for hover state to be active when there is no click handler attached
    ...restProps
  } = _ref;
  /**
   * Note (Nishant): Why we are using a `div` to render a onclick element instead of a `button`?
   *
   * Rendering the `Chip` component as a button directly would have been ideal, as it would
   * have naturally handled interactivity and accessibility for clickable chips(which has an onClick). However,
   * the `Chip` component includes a `CloseIcon`, which itself is a button. Nesting a `<button>`
   * inside another `<button>` is semantically incorrect and would lead to improper HTML structure.
   *
   * Instead, we use a `<div>` with `role="button"` to maintain proper semantic behavior and
   * avoid nesting buttons. While `role="button"` provides the appropriate semantics, it does
   * not automatically handle keyboard interactions. Therefore, we manually handle `Enter`
   * and `Space` key events to ensure the component is fully accessible and keyboard-compliant.
   *
   * Although this method might seem less conventional, it simplifies implementation and ensures
   * backward compatibility while adhering to accessibility standards.
   */
  const handleKeyDown = event => {
    const {
      key
    } = event;
    if (key === 'Enter' || key === ' ') {
      event.preventDefault(); // Prevent default action for Enter and Space keys
      onClick?.(event);
    }
  };
  return /*#__PURE__*/React.createElement("div", _extends({
    "data-testid": "Chip"
  }, restProps, {
    ref: ref,
    className: (0, _classify.classify)(_ChipModule.default.chipWrapper, {
      [_ChipModule.default.primary]: semantic === CHIP_SEMANTIC.primary,
      [_ChipModule.default.information]: semantic === CHIP_SEMANTIC.information,
      [_ChipModule.default.success]: semantic === CHIP_SEMANTIC.success,
      [_ChipModule.default.warning]: semantic === CHIP_SEMANTIC.warning,
      [_ChipModule.default.danger]: semantic === CHIP_SEMANTIC.danger,
      [_ChipModule.default.secondary]: semantic === CHIP_SEMANTIC.secondary,
      [_ChipModule.default.large]: size === 'large',
      [_ChipModule.default.medium]: size === 'medium',
      [_ChipModule.default.small]: size === 'small',
      [_ChipModule.default.dismissable]: dismissable,
      [_ChipModule.default.withIcon]: !!iconName && size !== 'small',
      [_ChipModule.default.disabled]: disabled,
      [_ChipModule.default.noHoverState]: showStatusIndicator || disableHoverState
    }, classNames?.wrapper),
    onClick: onClick,
    onKeyDown: handleKeyDown,
    tabIndex: showStatusIndicator || disableHoverState ? undefined : 0,
    role: showStatusIndicator || disableHoverState ? undefined : 'button'
  }), showStatusIndicator && size !== 'small' && /*#__PURE__*/React.createElement(_StatusIndicator.StatusIndicator, {
    status: semantic,
    classNames: {
      wrapper: (0, _classify.classify)(_ChipModule.default.statusIndicatorBlock, classNames?.statusIndicator)
    },
    disabled: disabled
  }), iconName && size !== 'small' && /*#__PURE__*/React.createElement(_Icon.Icon, {
    className: (0, _classify.classify)(_ChipModule.default.chipIcon, classNames?.icon),
    name: iconName,
    type: iconType,
    size: "small"
  }), /*#__PURE__*/React.createElement(_Truncate.Truncate, null, children), dismissable && size !== 'small' && /*#__PURE__*/React.createElement(_Icon.CloseIcon, {
    classNames: {
      icon: _ChipModule.default.dismissIcon,
      button: _ChipModule.default.dismissIconWrapper
    },
    type: iconType,
    size: "small",
    ariaLabel: "Dismiss",
    onClick: event => {
      event.stopPropagation();
      if (!disabled && onDismiss) {
        onDismiss(event);
      }
    }
  }));
});
exports.Chip = Chip;
Chip.displayName = 'Chip';